import { t } from 'i18next';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { MdFirstPage, MdLastPage, MdChevronLeft, MdChevronRight, MdSearch, MdClose, MdRefresh } from 'react-icons/md';

import NoData from '../../../components/NoData';
import { backendErrorNotification } from '../../../components/Notification';
import HttpStatus from '../../../enums/httpStatus';
import LocalStorageKeys from '../../../enums/localStorage';
import usePersistedState from '../../../hooks/usePersistedState';
import { FilterIcon, SettingIcon } from '../../../icons';
import { checkIfUseEmailSendingApi, ordersApi } from '../../../services/requests';
import ContentContainter from '../../../templates/Content/ContentContainer';
import { Order } from '../../../types/apiResponse/order';
import OrderFiltersModal from './Components/OrderFiltersModal';
import OrdersColumnsConfig from './Components/OrdersColumnsConfig';
import OrdersTableActions from './Components/Table/OrdersTableActions';
import OrderTableData from './Components/Table/OrderTableData';
import { ColumnConfig, defaultColumnsConfig } from './constants/defaultColumnsConfig';
import * as s from './OrdersPageStyles';
import useOrderFilters from './hooks/useOrderFilters';
import { OrderMoneyPrecisionProvider } from './OrderMoneyPrecision';

const OrdersPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const theme = useTheme();

  const [orders, setOrders] = useState<Order[]>([]);
  const [isEmailSendingUsedWhenMakeOrder, setIsEmailSendingUsedWhenMakeOrder] = useState(false);
  const [storedColumnsConfig, setStoredColumnsConfig] = usePersistedState<ColumnConfig[]>(
    LocalStorageKeys.ORDER_COLUMN_CONFIG,
    defaultColumnsConfig,
  );
  const [orderFilters, setOrderFilters] = useOrderFilters();
  const [orderNumberSearch, setOrderNumberSearch] = useState(
    orderFilters.orderNumber ?? '',
  );

  const searchOrderNumber = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const orderNumber = orderNumberSearch.trim();
    setOrderFilters({
      ...orderFilters,
      filterType: orderNumber ? 'simple' : 'detailed',
      orderNumber,
    });
  };

  const clearOrderNumberSearch = () => {
    setOrderNumberSearch('');
    setOrderFilters({ ...orderFilters, filterType: 'detailed', orderNumber: '' });
  };

  const defaultQtyPerPage = 14;

  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [quantityPerPage, setQuantityPerPage] = useState(defaultQtyPerPage);
  const [totalPages, setTotalPages] = useState(0);

  const [currentPageNumberInput, setCurrentPageNumberInput] = useState(1);
  const [quantityPerPageInput, setQuantityPerPageInput] = useState(defaultQtyPerPage);

  const [showTableConfig, setShowTableConfig] = useState(false);
  const [showOrderFilters, setShowOrderFilters] = useState(false);

  const [selectedOrders, setSelectedOrders] = useState<Order[]>([]);

  const getOrderQueryByOrderFilters = (): string => {
    if (orderFilters.filterType === 'simple' && orderFilters.orderNumber) {
      return `&orderNumber=${encodeURIComponent(orderFilters.orderNumber)}`;
    }

    let query = orderFilters.orderNumber
      ? `&orderNumber=${encodeURIComponent(orderFilters.orderNumber.trim())}`
      : '';

    if (orderFilters.orderStatus) {
      query += `&orderStatus=${orderFilters.orderStatus}`;
    }

    if (typeof orderFilters.isBudget === 'boolean') {
      query += `&isBudget=${orderFilters.isBudget}`;
    }

    if (orderFilters.orderTypeCode) {
      query += `&orderTypeCode=${orderFilters.orderTypeCode}`;
    }

    if (orderFilters.orderOriginCode && orderFilters.orderOriginCode !== 'all') {
      query += `&orderOriginCode=${orderFilters.orderOriginCode}`;
    }

    if (orderFilters.tenantManagerCode) {
      query += `&tenantManagerCode=${orderFilters.tenantManagerCode}`;
    }

    if (orderFilters.tenantSupervisorCode) {
      query += `&tenantSupervisorCode=${orderFilters.tenantSupervisorCode}`;
    }

    if (orderFilters.sellerCode) {
      query += `&sellerCode=${orderFilters.sellerCode}`;
    }

    if (orderFilters.customerCode) {
      query += `&customerCode=${orderFilters.customerCode}`;
    }

    if (orderFilters.billingUnitCode) {
      query += `&billingUnitCode=${orderFilters.billingUnitCode}`;
    }

    if (orderFilters.freightType && orderFilters.freightType != 'all') {
      query += `&freightType=${orderFilters.freightType}`;
    }

    if (orderFilters.exportedErp && orderFilters.exportedErp != 'all') {
      query += `&exportedErp=${orderFilters.exportedErp}`;
    }

    if (orderFilters.initialOrderCreationDate) {
      query += `&initialOrderCreationDate=${orderFilters.initialOrderCreationDate}`;
    }
    if (orderFilters.finalOrderCreationDate) {
      query += `&finalOrderCreationDate=${orderFilters.finalOrderCreationDate}`;
    }

    if (orderFilters.initialOrderSendDate) {
      query += `&initialOrderSendDate=${orderFilters.initialOrderSendDate}`;
    }
    if (orderFilters.finalOrderSendDate) {
      query += `&finalOrderSendDate=${orderFilters.finalOrderSendDate}`;
    }

    if (orderFilters.initialOrderDeliveryDate) {
      query += `&initialOrderDeliveryDate=${orderFilters.initialOrderDeliveryDate}`;
    }
    if (orderFilters.finalOrderDeliveryDate) {
      query += `&finalOrderDeliveryDate=${orderFilters.finalOrderDeliveryDate}`;
    }

    if (orderFilters.initialOrderBillingDate) {
      query += `&initialOrderBillingDate=${orderFilters.initialOrderBillingDate}`;
    }
    if (orderFilters.finalOrderBillingDate) {
      query += `&finalOrderBillingDate=${orderFilters.finalOrderBillingDate}`;
    }

    return query;
  };

  const [query, setQuery] = useState(getOrderQueryByOrderFilters());

  const getOrders = async () => {
    try {
      ordersApi.query = `?page=${currentPageNumber}&quantityPerPage=${quantityPerPage}${query}`;
      ordersApi.query += '&withEmailLog=true';
      ordersApi.query += '&withItems=true&withPendencies=true&withDetails=true';

      const response = await ordersApi.index();

      if (response.status === HttpStatus.OK) {
        setOrders(response.data.data);
        setTotalPages(response.data.paginated.totalPage);
      } else {
        throw response;
      }
    } catch (erro) {
      backendErrorNotification(erro as any);
    }
  };

  const getIfUseEmailSending = async () => {
    try {
      ordersApi.query = '';

      const response = await checkIfUseEmailSendingApi.show('make-an-order');

      if (response.status === HttpStatus.OK) {
        setIsEmailSendingUsedWhenMakeOrder(response.data.data);
      } else {
        throw response;
      }
    } catch (erro) {
      backendErrorNotification(erro as any);
    }
  };

  const loadData = async () => {
    setIsLoading(true);
    await getOrders();
    await getIfUseEmailSending();
    setIsLoading(false);
  };

  const handlePageInputValue = (text: string) => {
    let numericValue = text.replace(/[^0-9]/g, '') as unknown as number;

    if (numericValue > totalPages) {
      numericValue = totalPages;
    }

    setCurrentPageNumberInput(numericValue);
  };

  const handleQuantityInputValue = (text: string) => {
    let numericValue = text.replace(/[^0-9]/g, '') as unknown as number;

    if (numericValue > 50) {
      numericValue = 50;
    }
    setQuantityPerPageInput(numericValue);
  };

  const handleNavigationInputKeys = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (currentPageNumber <= 0) {
        setCurrentPageNumber(1);
        setCurrentPageNumberInput(1);
      }

      if (quantityPerPage <= 0) {
        setQuantityPerPage(defaultQtyPerPage);
        setQuantityPerPageInput(defaultQtyPerPage);
      }

      setCurrentPageNumber(currentPageNumberInput);
      setQuantityPerPage(quantityPerPageInput);
    }
  };

  useEffect(() => {
    setQuery(getOrderQueryByOrderFilters());
    setCurrentPageNumber(1);
  }, [orderFilters]);

  useEffect(() => {
    setOrderNumberSearch(orderFilters.orderNumber ?? '');
  }, [orderFilters.orderNumber]);

  useEffect(() => {
    loadData();
    setCurrentPageNumberInput(currentPageNumber);
    setQuantityPerPageInput(quantityPerPage);
  }, [currentPageNumber, quantityPerPage, query]);

  return (
    <OrderMoneyPrecisionProvider>
      <ContentContainter
        title={t('orders')}
        isLoading={isLoading}
        actions={[
          <s.HeaderSearchForm onSubmit={searchOrderNumber} role="search" key="orderNumberSearch">
            <input
              type="text"
              inputMode="numeric"
              aria-label={t('orderNumber')}
              placeholder={t('orderNumber')}
              value={orderNumberSearch}
              onChange={(event) => setOrderNumberSearch(event.target.value)}
            />
            {(orderNumberSearch || orderFilters.orderNumber) && (
              <button
                type="button"
                onClick={clearOrderNumberSearch}
                aria-label={t('clear')}
                title={t('clear')}
                disabled={isLoading}
              >
                <MdClose aria-hidden="true" />
              </button>
            )}
            <button type="submit" aria-label={t('search')} title={t('search')} disabled={isLoading}>
              <MdSearch aria-hidden="true" />
            </button>
          </s.HeaderSearchForm>,
          <s.HeaderActionButton
            onClick={() => setShowTableConfig(!showTableConfig)}
            title={t('tableConfig')}
            key="tableConfig"
          >
            <SettingIcon color={theme.colors.onPrimary} size="1.2rem" />
          </s.HeaderActionButton>,
          <s.HeaderActionButton
            onClick={() => setShowOrderFilters(true)}
            title={t('toFilter')}
            key="toFilter"
          >
            <FilterIcon color={theme.colors.onPrimary} size="1.2rem" />
          </s.HeaderActionButton>,
          <s.HeaderActionButton
            onClick={() => void loadData()}
            title={t('update')}
            aria-label={t('update')}
            aria-busy={isLoading}
            disabled={isLoading}
            key="refreshOrders"
          >
            <MdRefresh aria-hidden="true" />
          </s.HeaderActionButton>,
        ]}
      >
        <s.OrdersPageWrapper>
          {orders.length >= 1 ? (
            <>
              {/* Table Wrapper */}
              <div className="table-wrapper">
                <div className="table-body">
                  <div className="orders-table-container">
                    <OrdersTableActions
                      orders={orders}
                      reloadOrders={loadData}
                      isEmailSendingUsedWhenMakeOrder={isEmailSendingUsedWhenMakeOrder}
                      selectedItems={selectedOrders}
                      onChangeSelectedItems={setSelectedOrders}
                    />
                    <OrderTableData
                      orders={orders}
                      columnsConfig={storedColumnsConfig}
                      fixed="show"
                      selectedItems={selectedOrders}
                    />
                    <OrderTableData
                      orders={orders}
                      columnsConfig={storedColumnsConfig}
                      fixed="hide"
                      selectedItems={selectedOrders}
                    />
                  </div>
                </div>
              </div>

              {/* Modern Pagination */}
              <div className="navigation-bar">
                <div className="pagination-wrapper">
                  <button
                    className={`page-button ${currentPageNumber <= 1 ? 'disabled' : ''}`}
                    onClick={() => currentPageNumber > 1 && setCurrentPageNumber(1)}
                    title={t('firstPage')}
                    disabled={currentPageNumber <= 1}
                  >
                    <MdFirstPage />
                  </button>
                  <button
                    className={`page-button ${currentPageNumber <= 1 ? 'disabled' : ''}`}
                    onClick={() => {
                      const newPage = currentPageNumber - 1;
                      if (newPage >= 1) setCurrentPageNumber(newPage);
                    }}
                    title={t('previousPage')}
                    disabled={currentPageNumber <= 1}
                  >
                    <MdChevronLeft />
                  </button>

                  <input
                    className="page-input"
                    type="text"
                    value={currentPageNumberInput}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handlePageInputValue(e.currentTarget.value)
                    }
                    onKeyDown={handleNavigationInputKeys}
                    title={t('goToPage')}
                  />

                  <span className="page-info">/ {totalPages}</span>

                  <button
                    className={`page-button ${currentPageNumber >= totalPages ? 'disabled' : ''}`}
                    onClick={() => {
                      const newPage = currentPageNumber + 1;
                      if (newPage <= totalPages) setCurrentPageNumber(newPage);
                    }}
                    title={t('nextPage')}
                    disabled={currentPageNumber >= totalPages}
                  >
                    <MdChevronRight />
                  </button>
                  <button
                    className={`page-button ${currentPageNumber >= totalPages ? 'disabled' : ''}`}
                    onClick={() => setCurrentPageNumber(totalPages)}
                    title={t('lastPage')}
                    disabled={currentPageNumber >= totalPages}
                  >
                    <MdLastPage />
                  </button>
                </div>

                <div className="per-page-wrapper">
                  <span className="per-page-label">{t('quantityPerPage') as string}:</span>
                  <input
                    className="page-input"
                    type="text"
                    value={quantityPerPageInput}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleQuantityInputValue(e.currentTarget.value)
                    }
                    onKeyDown={handleNavigationInputKeys}
                    style={{ width: '3.5rem' }}
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="empty-state">
              <NoData />
            </div>
          )}

          <OrdersColumnsConfig
            closeModal={() => setShowTableConfig(false)}
            visible={showTableConfig}
            columnsConfig={storedColumnsConfig}
            setColumnsConfig={(value) => setStoredColumnsConfig(value)}
          />
        </s.OrdersPageWrapper>
      </ContentContainter>

      {showOrderFilters && (
        <OrderFiltersModal
          closeModal={() => setShowOrderFilters(false)}
          setStoredOrderFilters={(value) => {
            setOrderFilters(value);
            setShowOrderFilters(false);
          }}
          isLoading={false}
        />
      )}
    </OrderMoneyPrecisionProvider>
  );
};

export default OrdersPage;
